//src\services\creation\ObjectCreation.ts
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry';
import * as SphericalArrangement from '../abilities/SphericalArrangement';
// at top of ObjectCreation.ts
import { Reflector } from 'three/examples/jsm/objects/Reflector.js';
//This class is only responsible for creating and returning Meshes to be added to the scene. It is ok to set the positions of the Meshes as well.
export function transparentSphere(radius) {
    const color = THREE.MathUtils.randInt(0, 0xffffff);
    const sphereGeometry = new THREE.SphereGeometry(radius, 64, 64).toNonIndexed();
    const sphereMaterial = new THREE.MeshBasicMaterial({
        color: color,
        transparent: true,
        opacity: 0.2,
        wireframe: false,
        side: THREE.BackSide
    });
    const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
    return sphere;
}
export function roundedBox(position) {
    const roundedBoxGeometry = new RoundedBoxGeometry(1, 1, 1, 4, 0.1);
    const roundedBoxMaterial = new THREE.MeshNormalMaterial({ wireframe: true });
    const roundedBoxMesh = new THREE.Mesh(roundedBoxGeometry, roundedBoxMaterial);
    roundedBoxMesh.position.x = position.x;
    roundedBoxMesh.position.y = position.y;
    roundedBoxMesh.position.z = position.z;
    return roundedBoxMesh;
}
/**
 * Creates a giant mirror floor that spans the arena size.
 * @param arenaSize The same arena size used for the original world floor.
 * @returns A Reflector instance positioned as a horizontal mirror.
 */
export function createMirrorFloorOld(arenaSize) {
    const width = arenaSize * 1000;
    const height = arenaSize * 1000;
    const geometry = new THREE.PlaneGeometry(width, height);
    // Mirror with a reasonable texture resolution (adjust as needed)
    const mirror = new Reflector(geometry, {
        clipBias: 0.003,
        textureWidth: window.innerWidth * window.devicePixelRatio,
        textureHeight: window.innerHeight * window.devicePixelRatio,
        color: 0x999999 //0x999999 // tint if desired; pure mirror: set to 0xFFFFFF #or 0x2ca3e8 
    });
    mirror.rotateX(-Math.PI / 2); // lay horizontally
    mirror.position.y = 0.01; // small offset to avoid z-fighting
    return mirror;
}
export function createMirrorFloor(arenaSize, opts) {
    var _a, _b, _c, _d, _e;
    const width = arenaSize * 1000;
    const height = arenaSize * 1000;
    const dpr = Math.min((_a = opts === null || opts === void 0 ? void 0 : opts.dpr) !== null && _a !== void 0 ? _a : window.devicePixelRatio, 1.5);
    const scale = (_b = opts === null || opts === void 0 ? void 0 : opts.textureScale) !== null && _b !== void 0 ? _b : 1.0;
    const geom = new THREE.PlaneGeometry(width, height);
    const mirror = new Reflector(geom, {
        clipBias: (_c = opts === null || opts === void 0 ? void 0 : opts.clipBias) !== null && _c !== void 0 ? _c : 0.003,
        textureWidth: Math.floor(window.innerWidth * dpr * scale),
        textureHeight: Math.floor(window.innerHeight * dpr * scale),
        color: new THREE.Color((_d = opts === null || opts === void 0 ? void 0 : opts.baseColor) !== null && _d !== void 0 ? _d : 0xffffff)
    });
    mirror.rotateX(-Math.PI / 2);
    mirror.material.side = THREE.FrontSide; // be explicit
    // Lift more than 0.01 to avoid fighting long axis lines / huge quads
    const y = (_e = opts === null || opts === void 0 ? void 0 : opts.y) !== null && _e !== void 0 ? _e : 0.05;
    mirror.position.y = y;
    // Draw mirror very early to reduce conflicts with post-process & outlines
    mirror.renderOrder = -1000;
    // OPTIONAL: a super-thin tint sheet above the mirror for precise "floor color"
    // This gives you independent control of hue/sat/opacity beyond the Reflector's color
    const tintGeom = new THREE.PlaneGeometry(width, height);
    const tintMat = new THREE.MeshBasicMaterial({
        color: 0x2ca3e8,
        transparent: true,
        opacity: 0.15, // softer than 0.2; tweak live
        //blending: THREE.MultiplyBlending // feels like a dyed surface
    });
    const tintOverlay = new THREE.Mesh(tintGeom, tintMat);
    tintOverlay.rotateX(-Math.PI / 2);
    tintOverlay.position.y = y + 0.01; // just above the mirror
    tintOverlay.renderOrder = -999; // just after the mirror, before most things
    // Depth bias so it never fights with the mirror:
    tintMat.polygonOffset = true;
    tintMat.polygonOffsetFactor = -1;
    tintMat.polygonOffsetUnits = -1;
    // Keep the mirror’s render target in sync on resize
    const onResize = () => {
        const newDpr = Math.min(window.devicePixelRatio, 1.5);
        const w = Math.floor(window.innerWidth * newDpr * scale);
        const h = Math.floor(window.innerHeight * newDpr * scale);
        // @ts-ignore - private-ish API
        mirror.getRenderTarget().setSize(w, h);
    };
    window.addEventListener('resize', onResize);
    return { mirror, tintOverlay };
}
export function createWorldFloor(arenaSize) {
    const planeGeometry = new THREE.PlaneGeometry(arenaSize * 1000, arenaSize * 1000).toNonIndexed();
    planeGeometry.rotateX(-Math.PI * 0.5);
    const planeMaterial = new THREE.MeshBasicMaterial({ color: 0x2ca3e8, side: THREE.DoubleSide, transparent: true, opacity: 0.2 });
    let plane = new THREE.Mesh(planeGeometry, planeMaterial);
    return plane;
}
export function createWorldAxisOld(axisSize, tickSize, tickDistance) {
    // Create material for the axis lines
    const material = new THREE.LineBasicMaterial({ color: 0xc1c8f7 });
    let verticalOffset = 0.01;
    // Create geometry for the axis lines
    const geometry = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(-10000, verticalOffset, 0),
        new THREE.Vector3(10000, verticalOffset, 0),
        new THREE.Vector3(0, verticalOffset, -10000),
        new THREE.Vector3(0, verticalOffset, 10000) // Z-axis endpoints
    ]);
    // Create axis lines
    const axisLines = new THREE.LineSegments(geometry, material);
    // Create notches for X-axis
    for (let i = -axisSize; i <= axisSize; i += tickDistance) {
        const notchGeometry = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(i, verticalOffset, tickSize),
            new THREE.Vector3(i, verticalOffset, -tickSize) // Adjust the height of the notch
        ]);
        const notchLine = new THREE.Line(notchGeometry, material);
        axisLines.add(notchLine);
    }
    // Create notches for Z-axis
    for (let i = -axisSize; i <= axisSize; i += tickDistance) {
        const notchGeometry = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(tickSize, verticalOffset, i),
            new THREE.Vector3(-tickSize, verticalOffset, i) // Adjust the height of the notch
        ]);
        const notchLine = new THREE.Line(notchGeometry, material);
        axisLines.add(notchLine);
    }
    return axisLines;
}
/**
 * createWorldAxis
 * - draws the X/Z axes with tick marks
 * - (optionally) adds a styled ground grid tuned per-world
 *
 * returns a THREE.Group so we can return both the axis lines and grid
 */
export function createWorldAxis(axisSize, tickSize, tickDistance, options = {}) {
    // resolve options with defaults
    const { axisColor = 0xc1c8f7, verticalOffset = 0.01, showGrid = true, gridSize = 40000, gridDivisions = 240, gridPrimaryColor = 0xb9cfff, gridSecondaryColor = 0x7d91f0, gridOpacity = 0.32, gridYOffset = 0.05, showSubdivisions = false, // default: ON
     } = options;
    const worldAxisGroup = new THREE.Group();
    //
    // AXIS LINES + TICKS
    //
    const axisMaterial = new THREE.LineBasicMaterial({ color: axisColor });
    const axisGeometry = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(-axisSize, verticalOffset, 0),
        new THREE.Vector3(axisSize, verticalOffset, 0),
        new THREE.Vector3(0, verticalOffset, -axisSize),
        new THREE.Vector3(0, verticalOffset, axisSize),
    ]);
    const axisLines = new THREE.LineSegments(axisGeometry, axisMaterial);
    if (tickDistance > 0) {
        // tick marks on X
        for (let i = -axisSize; i <= axisSize; i += tickDistance) {
            const notchGeometry = new THREE.BufferGeometry().setFromPoints([
                new THREE.Vector3(i, verticalOffset, tickSize),
                new THREE.Vector3(i, verticalOffset, -tickSize),
            ]);
            axisLines.add(new THREE.Line(notchGeometry, axisMaterial));
        }
        // tick marks on Z
        for (let i = -axisSize; i <= axisSize; i += tickDistance) {
            const notchGeometry = new THREE.BufferGeometry().setFromPoints([
                new THREE.Vector3(tickSize, verticalOffset, i),
                new THREE.Vector3(-tickSize, verticalOffset, i),
            ]);
            axisLines.add(new THREE.Line(notchGeometry, axisMaterial));
        }
    }
    worldAxisGroup.add(axisLines);
    //
    // GRID (optional)
    //
    if (showGrid) {
        // If subdivisions disabled, set divisions = 1 (only main axes)
        const divisions = showSubdivisions ? gridDivisions : 1;
        const grid = new THREE.GridHelper(gridSize, divisions, gridPrimaryColor, gridSecondaryColor);
        const gridMaterials = Array.isArray(grid.material)
            ? grid.material
            : [grid.material];
        gridMaterials.forEach(mat => {
            const m = mat;
            m.opacity = gridOpacity;
            m.transparent = true;
        });
        grid.position.y = gridYOffset;
        worldAxisGroup.add(grid);
    }
    return worldAxisGroup;
}
export function createRandomBoxes(numBoxes, arenaSize) {
    const boxes = [];
    for (let i = 0; i < numBoxes; i++) {
        const boxGeometry = new THREE.BoxGeometry(10, 10, 10).toNonIndexed();
        const boxMaterial = new THREE.MeshBasicMaterial({ color: 0x343aeb });
        let box = new THREE.Mesh(boxGeometry, boxMaterial);
        box.position.set(Math.floor(Math.random() * arenaSize), Math.floor(Math.random() * arenaSize), Math.floor(Math.random() * arenaSize));
        boxes.push(box);
    }
    return boxes;
}
export function createTransparentSpheres(numSpheres, arenaSize) {
    const transparentSpheres = [];
    for (let i = 0; i < numSpheres; i++) {
        const randomRadiusSize = Math.floor(Math.random() * 600);
        let sphere = transparentSphere(randomRadiusSize);
        const sizeFactor = arenaSize * 35;
        const xCoord = Math.floor((Math.random() - 0.5) * sizeFactor);
        const yCoord = randomRadiusSize + Math.floor(Math.random() * sizeFactor);
        const zCoord = Math.floor((Math.random() - 0.5) * sizeFactor);
        sphere.position.set(xCoord, yCoord, zCoord);
        transparentSpheres.push(sphere);
    }
    return transparentSpheres;
}
export function createOrbSphericalArrangement(outerRadius, numElements, centerCoords, objectGroup) {
    const positionedObjects = [];
    const coords = SphericalArrangement.sphericalArrangement(outerRadius, numElements, centerCoords);
    coords.forEach(point => {
        const clonedObject = objectGroup.clone();
        clonedObject.position.copy(point);
        positionedObjects.push(clonedObject);
    });
    return positionedObjects;
}
export function createRoundedRectangle(length, width, height, radius, rectColor, rectPostion) {
    // Create the geometry for the rounded rectangle
    const shape = new THREE.Shape();
    shape.moveTo(0, radius);
    shape.lineTo(0, height - radius);
    shape.quadraticCurveTo(0, height, radius, height);
    shape.lineTo(length - radius, height);
    shape.quadraticCurveTo(length, height, length, height - radius);
    shape.lineTo(length, radius);
    shape.quadraticCurveTo(length, 0, length - radius, 0);
    shape.lineTo(radius, 0);
    shape.quadraticCurveTo(0, 0, 0, radius);
    // Extrude the shape to give it depth
    const extrudeSettings = {
        steps: 2,
        depth: width,
        bevelEnabled: true,
        bevelThickness: radius,
        bevelSize: radius,
        bevelOffset: 0,
        bevelSegments: 16
    };
    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    // Create the material for the rectangle
    const material = new THREE.MeshBasicMaterial({ color: rectColor, side: THREE.DoubleSide });
    // Create the mesh using the geometry and material
    const rectangle = new THREE.Mesh(geometry, material);
    // Set the position of the rectangle
    rectangle.position.copy(rectPostion);
    // Return the created rectangle mesh
    return rectangle;
}
/**

export function createRoundedRectangleBackside(length: number, width: number, height: number, radius: number, rectColor: number, rectPostion: THREE.Vector3): THREE.Mesh
{
  // Create the geometry for the rounded rectangle
  const shape = new THREE.Shape();
  shape.moveTo(0, radius);
  shape.lineTo(0, height - radius);
  shape.quadraticCurveTo(0, height, radius, height);
  shape.lineTo(length - radius, height);
  shape.quadraticCurveTo(length, height, length, height - radius);
  shape.lineTo(length, radius);
  shape.quadraticCurveTo(length, 0, length - radius, 0);
  shape.lineTo(radius, 0);
  shape.quadraticCurveTo(0, 0, 0, radius);

  // Extrude the shape to give it depth
  const extrudeSettings = {
      steps: 2,
      depth: width,
      bevelEnabled: true,
      bevelThickness: radius,
      bevelSize: radius,
      bevelOffset: 0,
      bevelSegments: 16
  };

  const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

  // Create the material for the rectangle
  const material = new THREE.MeshBasicMaterial({ color: rectColor, side: THREE.BackSide });

  // Create the mesh using the geometry and material
  const rectangle = new THREE.Mesh(geometry, material);

  // Set the position of the rectangle
  rectangle.position.copy(rectPostion);
  
  // Return the created rectangle mesh
  return rectangle;
}




export function createRoundedCylinderBackside(
  radius: number,
  height: number,
  bevelRadius: number,
  cylColor: number,
  cylPosition: THREE.Vector3,
  radialSegments = 64,
  bevelSegments = 16
): THREE.Mesh {
  if (radius <= 0) throw new Error(`radius must be > 0`);
  if (height <= 0) throw new Error(`height must be > 0`);
  if (bevelRadius < 0) throw new Error(`bevelRadius must be >= 0`);
  // Clamp bevel so it can't exceed half the height (keeps a sensible fillet)
  const b = Math.min(bevelRadius, height / 2);

  // Circle shape centered at (0,0)
  const shape = new THREE.Shape();
  shape.absarc(0, 0, radius, 0, Math.PI * 2, false);

  const extrudeSettings: THREE.ExtrudeGeometryOptions = {
    steps: 1,
    depth: height,                // will become Y after rotation
    bevelEnabled: b > 0,
    bevelThickness: b,
    bevelSize: b,
    bevelOffset: 0,
    bevelSegments,
    curveSegments: radialSegments // smooth circle perimeter
  };

  const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
  // Center along the extrude axis (Z) then rotate so Z→Y
  geometry.translate(0, 0, -height / 2);
  geometry.rotateX(Math.PI / 2); // Z-depth becomes Y-height

  const material = new THREE.MeshBasicMaterial({
    color: cylColor,
    side: THREE.BackSide
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.copy(cylPosition);

  // Shadows (optional; you can swap to Phong if you want lighting/shadows)
  mesh.castShadow = true;
  mesh.receiveShadow = true;

  return mesh;
}

*/
/** === GEOMETRY BUILDERS (no materials/mesh) =============================== */
export function makeRoundedCylinderBacksideGeometry(radius, height, bevelRadius, radialSegments = 64, bevelSegments = 16) {
    if (radius <= 0)
        throw new Error(`radius must be > 0`);
    if (height <= 0)
        throw new Error(`height must be > 0`);
    if (bevelRadius < 0)
        throw new Error(`bevelRadius must be >= 0`);
    const b = Math.min(bevelRadius, height / 2);
    const shape = new THREE.Shape();
    shape.absarc(0, 0, radius, 0, Math.PI * 2, false);
    const extrudeSettings = {
        steps: 1,
        depth: height,
        bevelEnabled: b > 0,
        bevelThickness: b,
        bevelSize: b,
        bevelOffset: 0,
        bevelSegments,
        curveSegments: radialSegments
    };
    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    geometry.translate(0, 0, -height / 2); // center on Z
    geometry.rotateX(Math.PI / 2); // Z-depth -> Y height
    return geometry.toNonIndexed();
}
export function makeRoundedRectangleBacksideGeometry(length, width, height, radius, bevelSegments = 16) {
    if (length <= 0 || width <= 0 || height <= 0)
        throw new Error('dims must be > 0');
    const r = Math.min(radius, Math.min(length, height) / 2);
    const shape = new THREE.Shape();
    shape.moveTo(0, r);
    shape.lineTo(0, height - r);
    shape.quadraticCurveTo(0, height, r, height);
    shape.lineTo(length - r, height);
    shape.quadraticCurveTo(length, height, length, height - r);
    shape.lineTo(length, r);
    shape.quadraticCurveTo(length, 0, length - r, 0);
    shape.lineTo(r, 0);
    shape.quadraticCurveTo(0, 0, 0, r);
    const extrudeSettings = {
        steps: 1,
        depth: width,
        bevelEnabled: r > 0,
        bevelThickness: r,
        bevelSize: r,
        bevelOffset: 0,
        bevelSegments
    };
    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    // By convention: length→X, height→Y, width→Z (already aligned)
    geometry.translate(-length / 2, -height / 2, -width / 2); // center at origin
    return geometry.toNonIndexed();
}
/** === MESH CREATORS (unchanged API, now use geometry builders) ============ */
export function createRoundedCylinderBackside(radius, height, bevelRadius, cylColor, cylPosition, radialSegments = 64, bevelSegments = 16) {
    const geometry = makeRoundedCylinderBacksideGeometry(radius, height, bevelRadius, radialSegments, bevelSegments);
    const material = new THREE.MeshBasicMaterial({ color: cylColor, side: THREE.BackSide });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.copy(cylPosition);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    return mesh;
}
export function createRoundedRectangleBackside(length, width, height, radius, rectColor, rectPosition) {
    const geometry = makeRoundedRectangleBacksideGeometry(length, width, height, radius);
    const material = new THREE.MeshBasicMaterial({ color: rectColor, side: THREE.BackSide });
    const rectangle = new THREE.Mesh(geometry, material);
    rectangle.position.copy(rectPosition);
    return rectangle;
}
export function createCylinder(radius = 40, height = 200, cylinderColor = 0x0069f2, position = new THREE.Vector3(1000, 100, -1000)) {
    // Create the cylinder geometry
    const geometry = new THREE.CylinderGeometry(radius, radius, height, 64);
    // Create a Phong material for a less flat look
    const material = new THREE.MeshBasicMaterial({
        color: cylinderColor,
        //shininess: 1,  // Higher shininess for a glossy look
        //specular: 0x555555,  // Specular color for highlights
        side: THREE.DoubleSide
    });
    // Create the mesh with the geometry and material
    const cylinder = new THREE.Mesh(geometry, material);
    // Set the position using the position vector
    cylinder.position.copy(position);
    return cylinder;
}
export function createRoundedCylinder(radius = 40, totalHeight = 200, color = 0x0069f2, position = new THREE.Vector3(), bevelRadius = 10) {
    const straightHeight = totalHeight - 2 * bevelRadius;
    if (straightHeight <= 0) {
        throw new Error(`Bevel radius ${bevelRadius} too large for total height ${totalHeight}`);
    }
    const material = new THREE.MeshPhongMaterial({
        color,
        shininess: 100,
        specular: 0x555555,
        side: THREE.DoubleSide
    });
    // Main body
    const mainGeo = new THREE.CylinderGeometry(radius, radius, straightHeight, 64, 1, true);
    const mainMesh = new THREE.Mesh(mainGeo, material);
    mainMesh.position.y = 0;
    // Corrected caps
    // Bottom cap curves outward (shrinks toward bottom)
    const bottomCapGeo = new THREE.CylinderGeometry(radius, radius - bevelRadius / 2, bevelRadius, 64);
    const bottomCap = new THREE.Mesh(bottomCapGeo, material);
    bottomCap.position.y = -straightHeight / 2 - bevelRadius / 2;
    // Top cap curves outward (shrinks toward top)
    const topCapGeo = new THREE.CylinderGeometry(radius - bevelRadius / 2, radius, bevelRadius, 64);
    const topCap = new THREE.Mesh(topCapGeo, material);
    topCap.position.y = straightHeight / 2 + bevelRadius / 2;
    const group = new THREE.Group();
    group.add(mainMesh, topCap, bottomCap);
    group.position.copy(position);
    return group;
}
export function createDatabaseSymbol(radius = 40, height = 50, outerColor = 0x0069f2, innerColor = 0x125280, position = new THREE.Vector3(), bevelRadius = 5) {
    const group = new THREE.Group();
    const spacing = height / 8;
    const x = position.x, y = position.y, z = position.z;
    const bottomY = y;
    const middleY = y + spacing + height;
    const topY = y + 2 * (spacing + height);
    const bottom = createRoundedCylinder(radius, height, outerColor, new THREE.Vector3(x, bottomY, z), bevelRadius);
    const middle = createRoundedCylinder(radius, height, outerColor, new THREE.Vector3(x, middleY, z), bevelRadius);
    const top = createRoundedCylinder(radius, height, outerColor, new THREE.Vector3(x, topY, z), bevelRadius);
    const smallRadius = radius * 0.9;
    const gapHeight = spacing;
    const inner1Y = y + 0.5 * (height + spacing);
    const inner2Y = y + 1.5 * (height + spacing);
    const inner1 = createCylinder(smallRadius, gapHeight, innerColor, new THREE.Vector3(x, inner1Y, z));
    const inner2 = createCylinder(smallRadius, gapHeight, innerColor, new THREE.Vector3(x, inner2Y, z));
    group.add(bottom, middle, top, inner1, inner2);
    return group;
}
//expiremnt, might move into orb logic
/**
 * Create a mirror‑like reflective sphere.
 *
 * @param radius Size of the sphere.
 * @param near   Near plane for the CubeCamera.  Defaults to 1.
 * @param far    Far plane for the CubeCamera.  Defaults to 20000.
 * @param cubeRes Resolution of the cube render target.  Higher values give better quality at a cost.
 * @returns { mesh, cubeCamera } The sphere mesh and its cube camera.
 */
export function createReflectiveSphere(radius, near = 1, far = 20000, cubeRes = 512) {
    // geometry for the sphere
    const geometry = new THREE.SphereGeometry(radius, 64, 64);
    // render target for the cube camera
    const cubeRT = new THREE.WebGLCubeRenderTarget(cubeRes, {
        format: THREE.RGBAFormat,
        generateMipmaps: true,
        minFilter: THREE.LinearMipmapLinearFilter
    });
    // cube camera positioned at the sphere’s centre
    const cubeCamera = new THREE.CubeCamera(near, far, cubeRT);
    // material uses the cube render target’s texture as the environment map
    const material = new THREE.MeshBasicMaterial({
        envMap: cubeRT.texture
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.add(cubeCamera); // keep camera centred on the sphere
    return { mesh, cubeCamera };
}
